import { createFileRoute } from '@tanstack/react-router'
import { Link2, Tag, Type } from 'lucide-react'
import { useState } from 'react'

import {
  IMAGE_UPLOAD_MAX_BYTES,
  IMAGE_UPLOAD_MAX_MB,
  IMAGE_UPLOAD_MIME_TYPES,
} from '@brickup/shared/constants'
import type { McpUploadLinkDetail } from '@brickup/shared/dto'
import { MCP_UPLOAD_TARGET_LABELS } from '@brickup/shared/locales/de/enum-labels'
import { MCP } from '@brickup/shared/locales/de/mcp'
import { useMcpUploadLink, useRedeemMcpUploadLink } from '@/api/mcp'
import { Badge, BadgeRow, LoadingIndicator } from '@/components/base'
import { DetailRow } from '@/components/DetailRow'
import {
  PublicPageCard,
  PublicPageCardLoading,
  PublicPageCardMessage,
  PublicPageHeading,
  PublicPageText,
} from '@/components/Public/PublicPageCard'
import { FileDropzone } from '@/components/Upload/FileDropzone'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'
import { requireAuth } from '@/utils/require-auth'

const COPY = MCP.upload

export const Route = createFileRoute('/upload/$token')({
  // Signed in before anything is shown: the picture is written with this
  // operator's name against it, and the link only opens for the account the
  // assistant was acting as.
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.queryClient, location.href)
  },
  component: McpUploadScreen,
})

/**
 * The one step of a picture's journey an assistant cannot take itself.
 *
 * A tool call carries JSON, and a picture attached to a conversation reaches
 * the model as vision tokens rather than bytes — it never holds the file. So
 * it prepares everything around the file and hands over this page: the title,
 * the tags and the entry the picture belongs to are already set, and dropping
 * it is all that is left.
 */
function McpUploadScreen() {
  const { token } = Route.useParams()
  const { data, isLoading, isError } = useMcpUploadLink(token)
  const redeem = useRedeemMcpUploadLink(token)
  const showError = useMutationErrorSnackbar()
  const [done, setDone] = useState<{ duplicate: boolean } | null>(null)

  if (isLoading) return <PublicPageCardLoading />

  if (isError || !data) {
    return <PublicPageCardMessage variant="error" title={COPY.linkInvalid} />
  }

  if (done) {
    return (
      <PublicPageCardMessage
        variant="success"
        title={COPY.done}
        description={done.duplicate ? COPY.duplicate : undefined}
      />
    )
  }

  return (
    <PublicPageCard>
      <PublicPageHeading title={COPY.title} />
      <PublicPageText>{COPY.intro}</PublicPageText>

      <UploadPlan detail={data} />

      {redeem.isPending ? (
        <LoadingIndicator layout="block" text={COPY.uploading} />
      ) : (
        <FileDropzone
          multiple={false}
          accept={IMAGE_UPLOAD_MIME_TYPES}
          maxBytes={IMAGE_UPLOAD_MAX_BYTES}
          label={COPY.drop}
          sublabel={COPY.formats(IMAGE_UPLOAD_MAX_MB)}
          onFiles={files => {
            const file = files[0]
            if (!file) return
            redeem.mutate(file, {
              onSuccess: result =>
                setDone({ duplicate: result.duplicateOf !== null }),
              // Without this a refused upload — wrong format, too large, a
              // link that expired while the page was open — only flips the
              // dropzone back and says nothing.
              onError: error => showError(error, COPY.failed),
            })
          }}
        />
      )}
    </PublicPageCard>
  )
}

/** What the assistant already decided, so the operator can see it is right
 *  before dropping anything. */
function UploadPlan({ detail }: { detail: McpUploadLinkDetail }) {
  const target = MCP_UPLOAD_TARGET_LABELS[detail.target]

  return (
    <div className="flex flex-col">
      <DetailRow
        label={COPY.titleLabel}
        icon={Type}
        value={detail.title ?? COPY.noTitle}
      />
      <DetailRow
        label={COPY.forLabel}
        icon={Link2}
        value={detail.targetName ? `${target} — ${detail.targetName}` : target}
      />
      <DetailRow
        label={COPY.tagsLabel}
        icon={Tag}
        value={
          detail.tags.length > 0 ? (
            <BadgeRow align="center">
              {detail.tags.map(tag => (
                <Badge key={tag} size="sm" label={tag} />
              ))}
            </BadgeRow>
          ) : (
            COPY.noTags
          )
        }
      />
    </div>
  )
}
